import React, { useState, useRef, useEffect } from 'react';
import { UiTab, UiTable, UiButton, UiRangePicker, UiSearch, UiSelect, SupportDrawer } from '../../../components';
import { downloadReport, actionDots } from '../../../assets/images';
import { tabData, LAWYER_SUPPORT_COLUMNS, CUSTOMER_SUPPORT_COLUMNS, FEEDBACK_COLUMNS, supportStatusOptions, feedbackByOptions, actionDropdownMenu } from './config';
import { DatePicker, Dropdown, Menu, Space, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import SupportReport from './reports/supportReport';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LAWYERMANAGEMENT, CUSTOMERMANAGEMENT, SUPPORTMANAGEMENT, FEEDBACKMANAGEMENT, SENDMESSAGEWHATSAPPSUPPORT, SENDMESSAGEMAILSUPPORT } from "../../../apiServices/endpoints";
import { apiServices } from "../../../apiServices/interceptor";
import * as api from '../../../actions';
import { searchInitiateDelayTime, tablePageSizeOptions } from '../../../utils/appUtils';
import { showErrorNotification, showSuccessNotification } from '../../../components/uiNotification';
import { useDispatch } from 'react-redux';
import { loaderReducer } from '../../../reducer/loader.reducer';

const PrintSupportReport = React.forwardRef((props, ref) => {
	return <div ref={ref}>
		<SupportReport 
			data={props?.data} 
			date={props?.date} 
			reportType={props?.reportType}
            status={props?.status}
            selectedMonth={props?.selectedMonth}
            selectedMonthString={props?.selectedMonthString}
		/>
	</div>
});

const Support = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const reportRef = useRef(null);
    const debounceTimeOut = useRef(null);
    const defaultComposeState = { platforms: ['whatsapp'], subject: '', message: '' };
    const defaultProofImageModal = { open: false, imageUrls: [], currentIndex: 0, loading: false };
    const [compose, setCompose] = useState(defaultComposeState);
    const [completeModal, setCompleteModal] = useState({ open: false, supportId: null, loading: false });
    const [proofImageModal, setProofImageModal] = useState(defaultProofImageModal);
    const [screenState, setScreenState] = useState({
        activeTab: "LAWYERS",
        activeTabKey: "1",
        pageSize: { page: 0, size: 10 },
        date: { fromDate: "", toDate: "" },
        searchFilter: "",
        searchTermValue: "",
        selectMonth: null,
        selectedMonthString: "",
        supportData: [], 
        status: "PENDING",
        isReportOpen: false,
        openProfileView: false,
        selectedProfile: null,
        selectedComplaintId: null,
        feedbackBy: "LAWYER"
    });

    const getSupportDetails = (activeTab, date, searchTermValue, page, size, status, selectedMonthString) => api.get(`${SUPPORTMANAGEMENT}/page?page=${page}&size=${size}&complaintType=${activeTab === "LAWYERS" ? "COMPLAINT_RAISED_LAWYER" : "COMPLAINT_RAISED_CUSTOMER"}&isSupport=${true}${status ? `&complaintStatus=${status}` : ""}${selectedMonthString ? `&month=${selectedMonthString}` : ""}${date?.fromDate && date?.toDate ? `&fromDate=${date?.fromDate}&toDate=${date?.toDate}` : ""}${searchTermValue ? `&commonSearch=${searchTermValue}` : ""}`);
    const getComplaintDetailsById = (supportId) => api.get(`${SUPPORTMANAGEMENT}/${supportId}`);
    const getFeedbackDetails = (page, size, date, selectedMonthString, feedbackBy) => api.get(`${FEEDBACKMANAGEMENT}/page?page=${page}&size=${size}${feedbackBy ? `&feedbackBy=${feedbackBy}` : ""}${selectedMonthString ? `&month=${selectedMonthString}` : ""}${date?.fromDate && date?.toDate ? `&startDate=${date?.fromDate}&endDate=${date?.toDate}` : ""}`);
    const sendWhatsAppMessage = (payload) => api.post(SENDMESSAGEWHATSAPPSUPPORT, payload);
    const sendMailMessage = (payload) => api.post(SENDMESSAGEMAILSUPPORT, payload);
    const completeSupport = (supportId) => api.patch(`${SUPPORTMANAGEMENT}/move_support${supportId}`); 

    const { data: supportDetailsData, isFetching: isFetchingSupportDetails, refetch: fetchSupportDetails } = useQuery(
        ["FETCH_ALL_SUPPORT", screenState?.activeTab, screenState?.date, screenState?.searchTermValue, screenState?.pageSize?.page, screenState?.pageSize?.size, screenState?.status, screenState?.selectedMonthString, screenState?.feedbackBy],
        () => {
            if (screenState?.activeTab === "FEEDBACK") {
                return getFeedbackDetails(screenState?.pageSize?.page, screenState?.pageSize?.size, screenState?.date, screenState?.selectedMonthString, screenState?.feedbackBy);
            }
            return getSupportDetails(screenState?.activeTab, screenState?.date, screenState?.searchTermValue, screenState?.pageSize?.page, screenState?.pageSize?.size, screenState?.status, screenState?.selectedMonthString);
        },
        {
            enabled: Boolean(screenState?.activeTab),
            refetchOnWindowFocus: false
        }
    );

    const dataSource = screenState?.activeTab === "FEEDBACK" 
        ? supportDetailsData?.result?.feedbackResponses?.content || []
        : supportDetailsData?.result?.supportDetailsResponse?.content || [];

    const { isFetching: isFetchingComplaintById, refetch: refetchComplaintById } = useQuery(
        ["FETCH_COMPLAINT_DETAILS", screenState?.selectedComplaintId],
        () => getComplaintDetailsById(screenState?.selectedComplaintId),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200) {
                    const supportResponse = response?.result?.supportResponse;
                    const { raisedByName } = supportResponse;
                    const isLawyerTab = screenState?.activeTab === "LAWYERS";
                    const againstBaseUrl = isLawyerTab ? CUSTOMERMANAGEMENT : LAWYERMANAGEMENT;
                    const raisedBaseUrl = isLawyerTab ? LAWYERMANAGEMENT : CUSTOMERMANAGEMENT;
                    const againstByProfileUrl = supportResponse?.againstByProfileUrl ? `${againstBaseUrl}/file${supportResponse?.againstByProfileUrl}` : null;
                    const raisedByProfileUrl = supportResponse?.raisedByProfileUrl ? `${raisedBaseUrl}/file${supportResponse?.raisedByProfileUrl}` : null;
                    let platforms = ['whatsapp'];
                    if (supportResponse?.complaintStatus === 'COMPLETED' && supportResponse?.supportedIn) {
                        platforms = [];
                        if (supportResponse?.supportedIn?.includes('WHATSAPP')) {
                            platforms.push('whatsapp');
                        }
                        if (supportResponse?.supportedIn?.includes('EMAIL')) {
                            platforms.push('email');
                        }
                    }  
                    setScreenState(prev => ({
                        ...prev,
                        openProfileView: true,
                        selectedProfile: {
                            ...supportResponse,
                            complaintId: supportResponse?.supportId,
                            raisedById: supportResponse?.userId,
                            raisedByProfileUrl,
                            againstByProfileUrl,
                            profilePic: raisedByProfileUrl,
                            name: raisedByName,
                            lawyerId: supportResponse?.supportNo,
                            mobileNumber: supportResponse?.raisedByMobileNumber,
                            email: supportResponse?.raisedByEmail
                        }
                    }));
                    setCompose(prev => ({ ...prev, platforms: platforms, message: supportResponse?.actionItem || '', subject: supportResponse?.subjectActionItem || '' }));
                }
            },
            enabled: Boolean(screenState?.selectedComplaintId),
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingSendMessage } = useQuery(["SEND_MESSAGE_WHATSAPP", ""], sendWhatsAppMessage, {
        onSuccess: (sendResponse) => {
            if (sendResponse?.data?.statusCode === 200) {
                showSuccessNotification("Success", sendResponse?.data?.result?.message);
                handleProfileCancel();
                fetchSupportDetails();
            }
            else {
                showErrorNotification("Error", sendResponse?.response?.data?.error?.message || sendResponse?.data?.error?.message || sendResponse?.message || "Failed to send WhatsApp message");
            }
        },
        enabled: false,
        refetchOnWindowFocus: false
    });

    const { isFetching: isFetchingSendMail } = useQuery(["SEND_MESSAGE_MAIL", ""], sendMailMessage, {
        onSuccess: (mailResponse) => {
            if (mailResponse?.data?.statusCode === 200) {
                showSuccessNotification("Success", mailResponse?.data?.result?.message);
                handleProfileCancel();
                fetchSupportDetails();
            }
            else {
                showErrorNotification("Error", mailResponse?.response?.data?.error?.message || mailResponse?.data?.error?.message || mailResponse?.message || "Failed to send mail");
            }
        },
        enabled: false,
        refetchOnWindowFocus: false
    });

    const { isFetching: isFetchingCompleteSupport, refetch: refetchCompleteSupport } = useQuery(
        ["COMPLETE_SUPPORT", completeModal?.supportId],
        () => completeSupport(completeModal?.supportId),
        {
            onSuccess: (response) => {
                if (response?.data?.statusCode === 200) {
                    showSuccessNotification("Success", response?.data?.result?.message || "Support completed successfully");
                    setCompleteModal({ open: false, supportId: null, loading: false });
                    fetchSupportDetails();
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const handleTabChange = (key) => {
        const selectedTab = tabData?.find(tab => tab?.key === key);
        if (!selectedTab) return;
        setScreenState(prev => ({
            ...prev,
            activeTabKey: key,
            activeTab: selectedTab?.content,
            pageSize: { page: 0, size: 10 },
            date: { fromDate: "", toDate: "" },
            searchFilter: "",
            searchTermValue: "",
            selectMonth: null,
            selectedMonthString: "",
            status: "PENDING",
            feedbackBy: "LAWYER"
        }));
    };

    const handleSearchChange = (event) => {
        const searchTerm = event?.target?.value || "";
        setScreenState(prev => ({ ...prev, searchFilter: searchTerm }));
        debounceSearch(searchTerm);
    };

    const handleStatusChange = (value) => {
        setScreenState(prev => ({ ...prev, status: value, pageSize: { page: 0, size: 10 } }));
    };

    const handleFeedbackByChange = (value) => {
        setScreenState(prev => {
            return { ...prev, feedbackBy: value, pageSize: { page: 0, size: 10 } };
        });
    };

    const debounceSearch = (searchTerm) => {
        if (debounceTimeOut?.current && typeof debounceTimeOut?.current === 'number') {
            clearTimeout(debounceTimeOut?.current);
        }
        if (searchTerm && searchTerm !== "") {
            debounceTimeOut.current = setTimeout(() => {
                setScreenState(prev => ({
                    ...prev,
                    searchTermValue: searchTerm,
                    pageSize: { page: 0, size: 10 }
                }));
            }, searchInitiateDelayTime);
        } else {
            setScreenState(prev => ({
                ...prev,
                searchTermValue: "",
                pageSize: { page: 0, size: 10 }
            }));
        }
    };

    const handleMonthChange = (month) => {
        if (month) {
            const monthValue = dayjs(month).format("YYYY-MM");
            setScreenState(prev => ({
                ...prev,
                selectMonth: month,
                selectedMonthString: monthValue,
                date: { fromDate: "", toDate: "" }
            }));
        } else {
            setScreenState(prev => ({
                ...prev,
                selectMonth: null,
                selectedMonthString: ""
            }));
        }
    };

    const handleDateChange = (dates) => {
         setScreenState(prev => ({
            ...prev,
            date: {
                fromDate: dates ? dayjs(dates[0]).format("YYYY-MM-DD") : "",
                toDate: dates ? dayjs(dates[1]).format("YYYY-MM-DD") : ""
            },
            selectMonth: null,
            selectedMonthString: ""
        }));
    };

    const handlePagination = (page, size) => {
        setScreenState(prev => ({ ...prev, pageSize: { page: page - 1, size: size } }));
    };

    const handleReportPrint = () => {
        setScreenState(prev => ({ ...prev, pageSize: { ...prev.pageSize, size: -1 }, isReportOpen: true }));
    };
    
    const handlePrint = useReactToPrint({
        content: () => reportRef?.current,
        onAfterPrint: () => setScreenState(prev => ({ ...prev, isReportOpen: false, pageSize: { ...prev?.pageSize, size: 10 } })),
        pageStyle: ` body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        .print-page::before {
            content: "";
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border: 2px solid #005D60;
            z-index: 9999;
            pointer-events: none;
        }`
    });

    const handleProfileView = (profileData) => {
         if (screenState?.selectedComplaintId === profileData?.supportId) {
            refetchComplaintById();
        } else {
            setScreenState(prev => ({ 
                ...prev, 
                selectedComplaintId: profileData?.supportId
            }));
        }
    };

    const handleProfileCancel = () => {
        setScreenState(prev => ({ 
            ...prev, 
            openProfileView: false,
            selectedProfile: null,
            selectedComplaintId: null
        }));
        setCompose(defaultComposeState);
    };

    const handleSendMessage = async () => {
        const userId = sessionStorage.getItem("userId");
        const selectedPlatforms = compose?.platforms || [];
        const basePayload = {
            body: compose?.message,
            supportId: screenState?.selectedProfile?.supportId || "",
            complaintType: screenState?.selectedProfile?.complaintType || "",
            userId: screenState?.selectedProfile?.userId || "",
            subject: compose?.subject,
            actionItemTakenById: userId
        };
        if (selectedPlatforms?.includes('whatsapp')) {
            queryClient.prefetchQuery(["SEND_MESSAGE_WHATSAPP", ""], () => sendWhatsAppMessage({ ...basePayload, supportedIn: 'WHATSAPP' }));
        }
        if (selectedPlatforms?.includes('email')) {
            queryClient.prefetchQuery(["SEND_MESSAGE_MAIL", ""], () => sendMailMessage({ ...basePayload, supportedIn: 'EMAIL' }));
        }
    };

    const handleAction = (menuType, detail) => {
        if (menuType === "view") {
            handleProfileView(detail);
        } else if (menuType === "complete") {
            setCompleteModal({ open: true, supportId: detail?.supportId, loading: false });
        }
    };

    const handleConfirmComplete = () => {
        if (completeModal?.supportId) {
            setCompleteModal(prev => ({ ...prev, loading: true }));
            refetchCompleteSupport();
        }
    };

    const revokeProofImageUrls = (imageUrls) => {
        if (!Array.isArray(imageUrls) || !imageUrls?.length) return;
        imageUrls?.forEach((imageUrl) => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        });
    };

    const onViewProofImage = (attachments) => {
        const fileNames = Array.isArray(attachments)
            ? attachments?.filter((attachment) => Boolean(attachment))
            : attachments
                ? [attachments]
                : [];
        if (!fileNames?.length) return;
        revokeProofImageUrls(proofImageModal?.imageUrls);
        setProofImageModal({ ...defaultProofImageModal, open: true, loading: true });
        Promise.allSettled(
            fileNames?.map((fileName) => apiServices({
                url: `${SUPPORTMANAGEMENT}/file${fileName}`,
                method: 'get',
                responseType: 'blob'
            }))
        ).then((responses) => {
            const objectUrls = responses?.reduce((urls, response) => {
                if (response?.status !== 'fulfilled') {
                    return urls;
                }
                const blob = new Blob([response?.value?.data], { type: response?.value?.headers?.['content-type'] });
                const blobUrl = URL.createObjectURL(blob);
                return [...urls, blobUrl];
            }, []);
            setProofImageModal({
                open: true,
                imageUrls: objectUrls,
                currentIndex: 0,
                loading: false
            });
        }).catch(() => {
            setProofImageModal({ ...defaultProofImageModal, open: true, loading: false });
        });
    };

    const handleProofImageClose = () => {
        revokeProofImageUrls(proofImageModal?.imageUrls);
        setProofImageModal(defaultProofImageModal);
    };

    const handlePreviousProofImage = () => {
        setProofImageModal((previousState) => ({
            ...previousState,
            currentIndex: Math.max((previousState?.currentIndex || 0) - 1, 0)
        }));
    };

    const handleNextProofImage = () => {
        setProofImageModal((previousState) => {
            const maxIndex = Math.max((previousState?.imageUrls?.length || 1) - 1, 0);
            return {
                ...previousState,
                currentIndex: Math.min((previousState?.currentIndex || 0) + 1, maxIndex)
            };
        });
    };

    const menuList = (menuItems, detail) => {
        if (!menuItems || !Array.isArray(menuItems)) return <Menu></Menu>;
        return <Menu>
            {
                menuItems?.map((menuDetails) => {
                    if (!menuDetails || !menuDetails?.key) return null;
                    return <Menu.Item key={menuDetails?.key}
                        onClick={(event) => {
                            event?.domEvent?.stopPropagation();
                            handleAction(menuDetails?.key, detail);
                        }}
                    >{menuDetails?.label || ""}</Menu.Item>
                })
            }
        </Menu>
    };

    const actionRender = (detail) => {
        const filteredMenuItems = screenState?.status === "COMPLETED" 
            ? actionDropdownMenu?.filter(item => item?.key !== "complete")
            : actionDropdownMenu;
        return (
            <div style={{ textAlign: "center" }} onClick={(event) => event?.stopPropagation()}>
                <Dropdown overlay={menuList(filteredMenuItems, detail)} trigger={["click"]}>
                    <Space onClick={(event) => {
                        event?.preventDefault();
                        event?.stopPropagation();
                    }} style={{ cursor: "pointer" }}>
                        <img src={actionDots} alt="Action Dots" style={{ cursor: "pointer" }} />
                    </Space>
                </Dropdown>
            </div>
        );
    };

    const handleSupportRowClick = (supportDetail) => {
        if (screenState?.activeTab !== "FEEDBACK") {
            handleAction("view", supportDetail);
        }
    };

    useEffect(() => {
        dispatch(loaderReducer(isFetchingSupportDetails || isFetchingComplaintById || isFetchingSendMessage || isFetchingSendMail || isFetchingCompleteSupport));
    }, [dispatch, isFetchingSupportDetails, isFetchingComplaintById, isFetchingSendMessage, isFetchingSendMail, isFetchingCompleteSupport]);

    useEffect(() => {
        if (screenState?.isReportOpen) {
            handlePrint();
        }
    }, [screenState?.isReportOpen, handlePrint]);

    const totalSupportCount = screenState?.activeTab === "FEEDBACK"
        ? supportDetailsData?.result?.feedbackResponses?.totalElements ?? 0
        : supportDetailsData?.result?.supportDetailsResponse?.totalElements ?? 0;
    const shouldShowSupportPagination = totalSupportCount > 10;
    const totalProofImages = proofImageModal?.imageUrls?.length || 0;
    const activeProofImageUrl = totalProofImages > 0 ? proofImageModal?.imageUrls?.[proofImageModal?.currentIndex] : null;

    return (
        <div>
            <div className="flexible">
                <div className="flexible">
                    <h6 style={{ padding: "10px", margin: 0, whiteSpace: "nowrap" }}>Support</h6>
                </div>
                <div className="flexible">
                    {screenState?.activeTab !== "FEEDBACK" && <UiSelect 
                        options={supportStatusOptions}
                        value={screenState?.status}
                        onChange={handleStatusChange}
                        style={{ width: 150 }}
                        allowClear={false}
                    />}
                    {screenState?.activeTab === "FEEDBACK" && <UiSelect 
                        options={feedbackByOptions}
                        value={screenState?.feedbackBy}
                        onChange={handleFeedbackByChange}
                        style={{ width: 150 }}
                        allowClear={false}
                    />}
                    <DatePicker 
                        picker="month" 
                        format="MMM"
                        value={screenState?.selectMonth}
                        onChange={handleMonthChange}
                        disabledDate={(current) => current && current > dayjs().endOf("day")} 
                        style={{ height: 32, width: 150, borderRadius: 6 }}
                    />
                    <UiRangePicker 
                        onChange={handleDateChange}
                        value={screenState?.date?.fromDate ? [dayjs(screenState?.date?.fromDate), dayjs(screenState?.date?.toDate)] : []}
                        disabledDate={(current) => current && current > dayjs().endOf("day")}
                    />
                    {screenState?.activeTab !== "FEEDBACK" && <UiSearch 
                        placeholder="Support ID" 
                        value={screenState?.searchFilter} 
                        onChange={handleSearchChange} 
                    />}
                    {screenState?.activeTab !== "FEEDBACK" && <UiButton 
                        onClick={handleReportPrint} 
                        className="primary-btn"
                        type="primary"
                    >
                        <img src={downloadReport} alt="download" style={{ marginRight: 8 }} />
                        Download Report
                    </UiButton>}
                </div>
            </div>
            <UiTab 
                tabs={tabData} 
                activeTabKey={screenState?.activeTabKey} 
                onChange={handleTabChange} 
                count={screenState?.activeTab === "FEEDBACK" 
                    ? supportDetailsData?.result?.feedbackResponses?.totalElements ?? 0
                    : supportDetailsData?.result?.supportDetailsResponse?.totalElements ?? 0}
            />
            <UiTable 
                columns={screenState?.activeTab === "LAWYERS" 
                    ? LAWYER_SUPPORT_COLUMNS({ actionRender, supportStatusOptions: screenState?.status, onViewProofImage }) 
                    : screenState?.activeTab === "CUSTOMERS" 
                        ? CUSTOMER_SUPPORT_COLUMNS({ actionRender, supportStatusOptions: screenState?.status, onViewProofImage })
                        : FEEDBACK_COLUMNS} 
                dataSource={dataSource} 
                onRow={(supportDetail) => ({
                    onClick: () => handleSupportRowClick(supportDetail)
                })}
                scroll={{x: "max-content", y: "calc(100vh - 280px)" }}
                pagination={screenState?.isReportOpen || !shouldShowSupportPagination ? false : {
                    pageSize: screenState?.pageSize?.size || 10,
                    current: (screenState?.pageSize?.page ?? 0) + 1,
                    total: totalSupportCount,
                    pageSizeOptions: tablePageSizeOptions,
                    showSizeChanger: true,
                    onChange: (page, size) => handlePagination(page, size)
                }}
            />
            <SupportDrawer
                openProfileView={screenState?.openProfileView}
                handleProfileCancel={handleProfileCancel}
                supportProfileData={screenState?.selectedProfile || {}}
                activeTab={screenState?.activeTab}
                selectedPlatforms={compose?.platforms}
                setSelectedPlatforms={(value) => setCompose(prev => ({ ...prev, platforms: value }))}
                subject={compose?.subject}
                setSubject={(value) => setCompose(prev => ({ ...prev, subject: value }))}
                message={compose?.message}
                setMessage={(value) => setCompose(prev => ({ ...prev, message: value }))}
                handleSendMessage={handleSendMessage}
                destroyOnClose
            />
            <div style={{ display: "none" }}>
                <PrintSupportReport 
                    ref={reportRef} 
                    data={dataSource} 
                    date={screenState?.date} 
                    reportType={screenState?.activeTab}
                    status={screenState?.status}
                    selectedMonth={screenState?.selectMonth}
                    selectedMonthString={screenState?.selectedMonthString}
                />
            </div>
            <Modal
                open={completeModal?.open}
                onCancel={() => setCompleteModal({ open: false, supportId: null, loading: false })}
                footer={null}
                centered
                width={400}
                closable={false}
                className="complete-support-modal"
            >
                <div className="modal-content" style={{ textAlign: 'center', padding: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                        <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#faad14' }} />
                    </div>
                    <p style={{ fontSize: '16px', marginBottom: '24px' }}>Are you sure you want to complete this support?</p>
                    <div className="button-container" style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        <UiButton
                            onClick={() => setCompleteModal({ open: false, supportId: null, loading: false })}
                            disabled={completeModal?.loading}
                        >
                            Cancel
                        </UiButton>
                        <UiButton
                            type="primary"
                            loading={completeModal?.loading}
                            onClick={handleConfirmComplete}
                        >
                            Confirm
                        </UiButton>
                    </div>
                </div>
            </Modal>
            <Modal
                open={proofImageModal?.open}
                onCancel={handleProofImageClose}
                footer={null}
                centered
                width={600}
                title="Proof Image"
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    {proofImageModal?.loading ? (
                        <p>Loading...</p>
                    ) : activeProofImageUrl ? (
                        <>
                            <img 
                                src={activeProofImageUrl} 
                                alt={`Proof ${proofImageModal?.currentIndex + 1}`} 
                                style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
                                <UiButton
                                    onClick={handlePreviousProofImage}
                                    disabled={(proofImageModal?.currentIndex || 0) <= 0}
                                >
                                    Previous
                                </UiButton>
                                <span>{`${proofImageModal?.currentIndex + 1} / ${totalProofImages}`}</span>
                                <UiButton
                                    onClick={handleNextProofImage}
                                    disabled={(proofImageModal?.currentIndex || 0) >= totalProofImages - 1}
                                >
                                    Next
                                </UiButton>
                            </div>
                        </>
                    ) : (
                        <p>No image available</p>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default Support;
